import React, { useState } from "react";
import Modal from "react-responsive-modal";
import LogIn from "react-feather/dist/icons/log-in";
import LogOut from "react-feather/dist/icons/log-out";
import Mail from "react-feather/dist/icons/mail";

import style from "./style.module.scss";
import { Button, Spinner } from "../../../ui";
import { useAuth } from "../../../data/context/auth-context";
import {
  useLoginMutation,
  useLogoutMutation,
  useSendEmailLinkMutation,
} from "../../../data/queries/auth";
import { trackLogin } from "../../../analytics";

interface LoginButtonProps {
  isInverted?: boolean;
  className?: string;
}

const LoginButton: React.FC<LoginButtonProps> = ({ ...props }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const loginMutation = useLoginMutation();
  const logoutMutation = useLogoutMutation();
  const sendEmailLinkMutation = useSendEmailLinkMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const isLoading = authLoading || loginMutation.isPending || logoutMutation.isPending;

  if (isAuthenticated) {
    const onLogout = (e: React.MouseEvent) => {
      e.preventDefault();
      logoutMutation.mutate();
    };

    return (
      <Button onClick={onLogout} className={style.logout} {...props}>
        <LogOut />
      </Button>
    );
  }

  const openModal = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
    setEmailSent(false);
    setEmail("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEmailSent(false);
    setEmail("");
  };

  const handleGoogleLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    loginMutation.mutate(undefined, {
      onSuccess: () => {
        trackLogin("google");
        closeModal();
      },
    });
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    sendEmailLinkMutation.mutate(email, {
      onSuccess: () => setEmailSent(true),
    });
  };

  return (
    <>
      <Button isDisabled={isLoading} className={style.login} onClick={openModal} {...props}>
        {isLoading ? <Spinner className={style.spinner} /> : <LogIn />}
        Se connecter
      </Button>

      <Modal open={isModalOpen} onClose={closeModal} center classNames={{ modal: style.modal }}>
        {emailSent ? (
          <div className={style.content}>
            <h3 className={style.title}>Vérifiez votre email</h3>
            <p className={style.message}>
              Un lien de connexion a été envoyé à <strong>{email}</strong>
            </p>
            <p className={style.hint}>Cliquez sur le lien dans l'email pour vous connecter.</p>
            <Button onClick={closeModal} isPrimary className={style.closeButton}>
              Fermer
            </Button>
          </div>
        ) : (
          <div className={style.content}>
            <h3 className={style.title}>Se connecter</h3>

            <Button
              onClick={handleGoogleLogin}
              isDisabled={loginMutation.isPending}
              className={style.googleButton}
            >
              {loginMutation.isPending ? (
                <Spinner className={style.buttonSpinner} />
              ) : (
                <svg className={style.googleIcon} viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
              )}
              Continuer avec Google
            </Button>

            <div className={style.divider}>
              <span>ou</span>
            </div>

            <form onSubmit={handleEmailSubmit} className={style.emailForm}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Votre adresse email"
                className={style.emailInput}
                required
              />
              <Button
                type="submit"
                isPrimary
                isDisabled={sendEmailLinkMutation.isPending || !email}
                className={style.emailButton}
              >
                {sendEmailLinkMutation.isPending ? (
                  <Spinner className={style.buttonSpinner} />
                ) : (
                  <Mail />
                )}
                Envoyer le lien de connexion
              </Button>
            </form>

            {sendEmailLinkMutation.isError && (
              <p className={style.error}>Une erreur est survenue. Veuillez réessayer.</p>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default LoginButton;
